import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Result, Spin } from 'antd';
import styled from 'styled-components';

import { LanguageCode } from '@constants/languages';
import { ROUTES } from '@constants/routes';
import { isAllowedLanguage } from '@domain/language-dropdown';
import Layout from '@domain/layout';
import { apis } from '@services/api/Api';
import { useGetConfiguration } from '@services/visitor-management';
import { useConfigurationStore } from '@store/use-configuration-store';
import { useElearningWpRemoteStore } from '@store/use-ewr-store';
import { useFormStore } from '@store/use-form-store';
import { useUserStore } from '@store/use-user-store';

const Container = styled.div`
  width: 60vw;

  @media only screen and (max-width: 1200px) {
    width: 100%;
    padding-bottom: 10vh;
  }
`;

const Loader = (
  <Layout>
    <Spin
      size="large"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    />
  </Layout>
);

interface ConfigurationLoaderProps {
  friendlyUrl: string;
  module: string;
}

function ConfigurationLoader({
  friendlyUrl,
  module
}: ConfigurationLoaderProps) {
  const navigate = useNavigate();
  const { init, reset } = useConfigurationStore();
  const { data, isLoading, isSuccess, isError, error } = useGetConfiguration(
    friendlyUrl,
    module
  );

  useEffect(() => {
    if (!data || !isSuccess) return;
    init(data.configuration.base);
    navigate(ROUTES.VR_LOGIN, { replace: true });
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (isError) reset();
    // eslint-disable-next-line
  }, [isError]);

  if (isLoading) return Loader;

  if (isError) {
    return (
      <Layout>
        <Result
          status="error"
          title="Failed to load configuration"
          subTitle={
            (
              error as {
                response?: { data?: { errorMessage?: string } };
              } | null
            )?.response?.data?.errorMessage
          }
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <h1>Visma Onsite</h1>
        <p>Friendly URL: {friendlyUrl}</p>
        <p>Module: {module}</p>
      </Container>
    </Layout>
  );
}

interface ElearningWpRemoteEntryLoaderProps {
  friendlyUrl: string;
}

function ElearningWpRemoteEntryLoader({
  friendlyUrl
}: ElearningWpRemoteEntryLoaderProps) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { init } = useConfigurationStore();
  const { storeTokens } = useUserStore();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Authentication token is missing from the URL.');
      setPhase('error');
      return;
    }

    async function initElearningWpRemote() {
      try {
        init({
          friendly_url: friendlyUrl,
          module: 'elearning-wp-remote',
          main_module: ''
        });

        const loginResp = await apis.elearningWpRemote.login(friendlyUrl, {
          token: token!
        });
        const { accessToken, refreshToken } = loginResp.data;
        storeTokens({ accessToken, refreshToken });

        await apis.elearningWpRemote.generateOtp(friendlyUrl);
        setPhase('otp');
        navigate(ROUTES.EWR_OTP, { replace: true });
      } catch (e: unknown) {
        const apiError = e as {
          response?: { data?: { errorMessage?: string } };
        };
        setError(
          apiError?.response?.data?.errorMessage ??
            'Failed to initialize. Please check your link and try again.'
        );
        setPhase('error');
      }
    }

    initElearningWpRemote();
    // eslint-disable-next-line
  }, []);

  if (phase === 'loading') return Loader;

  if (phase === 'error') {
    return (
      <Layout>
        <Result status="error" title="Access failed" subTitle={error} />
      </Layout>
    );
  }

  return null;
}

interface ExternalElearningEntryLoaderProps {
  friendlyUrl: string;
}

function ExternalElearningEntryLoader({
  friendlyUrl
}: ExternalElearningEntryLoaderProps) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { init } = useConfigurationStore();
  const { storeTokens } = useUserStore();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Authentication token is missing from the URL.');
      setPhase('error');
      return;
    }

    async function initExternalElearning() {
      try {
        init({
          friendly_url: friendlyUrl,
          module: 'elearning-external',
          main_module: ''
        });

        const loginResp = await apis.externalElearning.login(friendlyUrl, {
          token: token!
        });
        const { accessToken, refreshToken } = loginResp.data;
        storeTokens({ accessToken, refreshToken });

        await apis.externalElearning.generateOtp(friendlyUrl);
        setPhase('otp');
        navigate(ROUTES.EEL_OTP, { replace: true });
      } catch (e: unknown) {
        const apiError = e as {
          response?: { data?: { errorMessage?: string } };
        };
        setError(
          apiError?.response?.data?.errorMessage ??
            'Failed to initialize. Please check your link and try again.'
        );
        setPhase('error');
      }
    }

    initExternalElearning();
    // eslint-disable-next-line
  }, []);

  if (phase === 'loading') return Loader;

  if (phase === 'error') {
    return (
      <Layout>
        <Result status="error" title="Access failed" subTitle={error} />
      </Layout>
    );
  }

  return null;
}

interface ElearningWpOnSiteEntryLoaderProps {
  friendlyUrl: string;
}

function ElearningWpOnSiteEntryLoader({
  friendlyUrl
}: ElearningWpOnSiteEntryLoaderProps) {
  const { init } = useConfigurationStore();
  const navigate = useNavigate();

  useEffect(() => {
    init({
      friendly_url: friendlyUrl,
      module: 'elearning-wp-onsite',
      main_module: ''
    });
    navigate(ROUTES.EWOS_LOGIN, { replace: true });
    // eslint-disable-next-line
  }, []);

  return Loader;
}

function VismaOnsite() {
  const { friendlyUrl, module } = useParams();
  const [searchParams] = useSearchParams();
  const language = searchParams.get('language');
  const { reset } = useConfigurationStore();
  const { resetProcess } = useFormStore();
  const { logout, changeLanguage } = useUserStore();
  const { reset: resetEwr } = useElearningWpRemoteStore();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    resetProcess();
    logout();
    reset();
    resetEwr();
    changeLanguage(
      language && isAllowedLanguage(language) ? language : LanguageCode.EN
    );
    setReady(true);
    // eslint-disable-next-line
  }, []);

  if (!friendlyUrl || !module) {
    return (
      <Layout>
        <Result
          status="error"
          title="Invalid URL"
          subTitle="Friendly URL and module are required."
        />
      </Layout>
    );
  }

  if (!ready) return Loader;

  if (module === 'elearning-wp-remote') {
    return <ElearningWpRemoteEntryLoader friendlyUrl={friendlyUrl} />;
  }

  if (module === 'elearning-external') {
    return <ExternalElearningEntryLoader friendlyUrl={friendlyUrl} />;
  }

  if (module === 'elearning-wp-onsite') {
    return <ElearningWpOnSiteEntryLoader friendlyUrl={friendlyUrl} />;
  }

  return <ConfigurationLoader friendlyUrl={friendlyUrl} module={module} />;
}

export default VismaOnsite;
